/**
 * trials-page - Page component for displaying active trials
 * Shows loading state, trial cards grid, empty state, and error state
 */
import { BaseElement } from '../base-element';
import { Signal } from 'signal-polyfill';
import { trialsApi } from '../../api/trials.api';
import type { TrialEventSummaryDto } from '../../api/types';
import './trial-card';

export type PageState = 'loading' | 'loaded' | 'empty' | 'error';

export class TrialsPage extends BaseElement {
  private trials = new Signal.State<TrialEventSummaryDto[]>([]);
  private loading = new Signal.State(true);
  private error = new Signal.State<string | null>(null);
  private pageState = new Signal.Computed<PageState>(() => {
    if (this.loading.get()) return 'loading';
    if (this.error.get()) return 'error';
    if (this.trials.get().length === 0) return 'empty';
    return 'loaded';
  });

  connectedCallback(): void {
    this.updateRender();
    this.loadTrials();
  }

  /**
   * Load trials from the API
   */
  async loadTrials(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.updateRender();

    try {
      const response = await trialsApi.list();

      if (response.error) {
        this.error.set(response.error.title || 'Failed to load trials');
      } else if (response.data) {
        // Filter to only active trials
        const activeTrials = response.data.filter(trial => trial.isActive);
        this.trials.set(activeTrials);
      } else {
        this.trials.set([]);
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to load trials';
      this.error.set(errorMessage);
    } finally {
      this.loading.set(false);
      this.updateRender();
    }
  }

  /**
   * Retry loading trials
   */
  private handleRetry(): void {
    this.loadTrials();
  }

  /**
   * Handle refresh button click in empty state
   */
  private handleRefresh(): void {
    this.loadTrials();
  }

  private renderLoading(): string {
    return `
      <div class="loading-container flex min-h-[400px] flex-col items-center justify-center px-4 py-12" data-testid="loading-state">
        <div class="spinner-wrapper mb-4">
          <dt-spinner size="large"></dt-spinner>
        </div>
        <p class="loading-text text-sm font-medium text-neutral-600 dark:text-neutral-400">Loading trials...</p>
      </div>
    `;
  }

  private renderError(): string {
    return `
      <div class="error-container flex min-h-[400px] items-center justify-center px-4 py-12" data-testid="error-state">
        <div class="error-card flex max-w-sm flex-col items-center rounded-2xl border border-error-500/20 bg-error-50 p-8 text-center dark:border-error-500/30 dark:bg-error-500/10">
          <div class="error-icon mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-error-500/10 text-error-500">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
            </svg>
          </div>
          <h3 class="error-title mb-2 text-xl font-bold text-neutral-900 dark:text-white">Unable to load trials</h3>
          <p class="error-text mb-6 text-sm text-neutral-600 dark:text-neutral-400">Something went wrong while fetching the data. Please check your connection and try again.</p>
          <button class="retry-btn inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-600" data-testid="retry-btn">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4">
              <path fill-rule="evenodd" d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z" clip-rule="evenodd"/>
            </svg>
            Retry
          </button>
        </div>
      </div>
    `;
  }

  private renderEmpty(): string {
    return `
      <div class="empty-container flex min-h-[400px] flex-col items-center justify-center px-4 py-12 text-center" data-testid="empty-state">
        <div class="empty-illustration mb-6 h-48 w-48">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" class="h-full w-full">
            <circle cx="100" cy="100" r="80" class="fill-neutral-100" />
            <path d="M100 60c-22.091 0-40 17.909-40 40s17.909 40 40 40 40-17.909 40-40-17.909-40-40-40z" class="fill-neutral-200" />
            <circle cx="85" cy="90" r="5" class="fill-neutral-500" />
            <circle cx="115" cy="90" r="5" class="fill-neutral-500" />
            <ellipse cx="100" cy="115" rx="15" ry="8" class="fill-neutral-500" />
            <path d="M70 75c-5-10-5-20 5-25" class="stroke-neutral-400" stroke-width="3" stroke-linecap="round" fill="none" />
            <path d="M130 75c5-10 5-20-5-25" class="stroke-neutral-400" stroke-width="3" stroke-linecap="round" fill="none" />
          </svg>
        </div>
        <h2 class="empty-title mb-2 text-2xl font-bold text-neutral-900 dark:text-white">No active trials at this time</h2>
        <p class="empty-text mb-6 max-w-xs text-base text-neutral-600 dark:text-neutral-400">Check back soon for new events or contact your local club.</p>
        <button class="refresh-btn inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white shadow-xs transition-colors duration-150 hover:bg-primary-600" data-testid="refresh-btn">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-5 w-5">
            <path fill-rule="evenodd" d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z" clip-rule="evenodd"/>
          </svg>
          Refresh
        </button>
      </div>
    `;
  }

  private renderTrials(): string {
    const trialsData = this.trials.get();

    return `
      <div class="trials-container" data-testid="trials-loaded">
        <header class="page-header mb-8">
          <h1 class="page-title mb-2 text-3xl font-bold text-neutral-900 dark:text-white">Active Trials</h1>
          <p class="page-subtitle text-sm text-neutral-600 dark:text-neutral-400">Find and register for upcoming stockdog trials</p>
        </header>

        <div class="trials-grid grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="trials-grid">
          ${trialsData.map(trial => `<trial-card data-trial-id="${this.esc(trial.trialEventId)}"></trial-card>`).join('')}
        </div>
      </div>
    `;
  }

  private updateRender(): void {
    const state = this.pageState.get();

    let content = '';
    switch (state) {
      case 'loading':
        content = this.renderLoading();
        break;
      case 'error':
        content = this.renderError();
        break;
      case 'empty':
        content = this.renderEmpty();
        break;
      case 'loaded':
        content = this.renderTrials();
        break;
    }

    this.render(`
      ${content}
    `);

    // Attach event listeners and set trial data
    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    // Retry button
    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => this.handleRetry());
    }

    // Refresh button
    const refreshBtn = this.$('[data-testid="refresh-btn"]');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => this.handleRefresh());
    }

    // Set trial data on trial-card elements
    const trialsData = this.trials.get();
    const trialCards = this.$$('trial-card');
    trialCards.forEach((card, index) => {
      if (trialsData[index]) {
        (card as any).trial = trialsData[index];
      }
    });
  }

  /**
   * Public getter for tests to access page state
   */
  getPageState(): PageState {
    return this.pageState.get();
  }

  /**
   * Public getter for tests to access trials
   */
  getTrials(): TrialEventSummaryDto[] {
    return this.trials.get();
  }
}

customElements.define('dt-trials-page', TrialsPage);
